import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Label } from "@/components/ui/baseUi/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import { type AddressAutofillRetrieveResponse } from "@mapbox/search-js-core"
import { AddressAutofill } from "@mapbox/search-js-react"
import { type BoxFormPageProps } from "@modules/box/components/boxCreationForm/boxFormFirstPage"
import { BoxFormTemplatePage } from "@modules/box/components/boxCreationForm/boxFormTemplatePage"
import { Config } from "@modules/config"
import { word } from "@modules/core/utils/i18n"
import { useCountrySubdivisionsListArray } from "@modules/language/utils/countries"
import { cn } from "@modules/lib/utils"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useState } from "react"

export const BoxFormSecondPage = ({
  handleSubmit,
  onPrevious,
  formik,
  getFormErrorMessage,
  disabled,
}: BoxFormPageProps) => {
  const countryOptions = useCountrySubdivisionsListArray()
  const [open, setOpen] = useState(false)

  return (
    <BoxFormTemplatePage
      onPrevious={onPrevious}
      handleSubmit={handleSubmit}
      disabled={disabled}
      pageNumber={2}
      header={{ title: word("box.creation.page.2.title") }}
    >
      <AddressAutofill
        accessToken={Config.MAPBOX_API_KEY}
        options={{ streets: false }}
        onRetrieve={(res: AddressAutofillRetrieveResponse) => {
          const feature = res.features[0]!
          const metadata = feature.properties.metadata as { iso_3166_1?: string; iso_3166_2?: string } | undefined

          let countryCode: string | undefined

          if (metadata?.iso_3166_1) {
            countryCode = metadata.iso_3166_1.toUpperCase()
          } else if (metadata?.iso_3166_2) {
            const parts = metadata.iso_3166_2.split("-")
            countryCode = parts[0]?.toUpperCase()
          } else {
            countryCode = feature.properties.country_code?.toUpperCase()
          }

          const foundCountry = countryCode ? countryOptions.find((item) => item.code === countryCode) : undefined

          if (foundCountry) formik.setFieldValue("country", foundCountry.code)
          formik.setFieldValue("location", {
            longitude: feature.geometry.coordinates[0],
            latitude: feature.geometry.coordinates[1],
          })
          formik.setFieldValue("postalCode", feature.properties.postcode)
          formik.setFieldValue("additionalAddress", feature.properties.address_line2)
          formik.setFieldValue("city", feature.properties.address_level2)
        }}
        theme={{
          variables: {
            unitHeader: "28px",
            fontFamily: '"Trenda", sans-serif',
            border: "2px solid #424242",
            borderRadius: "0",
            boxShadow: "0 0 1px 1px rgba(0,0,0,0.1)",
            colorText: "var(--color-white)",
            colorPrimary: "var(--primary-500)",
            colorSecondary: "var(--secondary-500)",
            colorBackgroundHover: "var(--neutral-300)",
            colorBackground: "var(--neutral-100)",
          },
          cssText: `
            .Results {
                z-index: 100000;
                border-radius: 20px;
            }
            .ResultsAttribution {
                  display: none;
                }
        `,
        }}
        popoverOptions={{ offset: 0 }}
      >
        <>
          <StandardInput
            name="address"
            isRequiredInput
            label={word("box.form.address.address.label")}
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("address")}
            inputProps={{
              placeholder: word("box.form.address.address.label"),
              onBlur: (e) => {
                if (formik.handleBlur) formik.handleBlur(e, "address")
              },
            }}
          />
        </>
      </AddressAutofill>

      <StandardInput
        name="additionalAddress"
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("additionalAddress")}
        inputProps={{
          autoComplete: "address-line2",
          placeholder: word("box.form.address.additional_address.placeholder"),
        }}
      />

      <StandardInput
        name="postalCode"
        isRequiredInput
        label={word("box.form.address.postalCode.label")}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("postalCode")}
        inputProps={{
          placeholder: word("box.form.address.postalCode.label"),
          onBlur: (e) => {
            if (formik.handleBlur) formik.handleBlur(e, "postalCode")
          },
          onChange: (e) => {
            if (formik.handleChange) formik.handleChange(e, "postalCode")
          },
        }}
      />

      <StandardInput
        name="city"
        isRequiredInput
        label={word("box.form.address.city.label")}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("city")}
        inputProps={{
          placeholder: word("box.form.address.city.label"),
          onBlur: (e) => {
            if (formik.handleBlur) formik.handleBlur(e, "city")
          },
          onChange: (e) => {
            if (formik.handleChange) formik.handleChange(e, "city")
          },
        }}
      />

      {/* Country input */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="country" className="uppercase">
          {word("box.form.address.country.label")}
        </Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button variant="outline" role="combobox" aria-expanded={open} className="h-11 w-full justify-between">
                {formik.values.country
                  ? countryOptions.find((countryOptions) => countryOptions.code === formik.values.country)?.label
                  : word("box.form.address.country.label")}

                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            }
          />
          <PopoverContent className="w-65 border-0 p-0" align="start">
            <Command
              className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
              filter={(value, search) => {
                if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase()))) return 1
                return 0
              }}
            >
              <CommandInput placeholder={word("global.searchCountry")} className="text-textDefault-body" />
              <CommandList>
                <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                <CommandGroup>
                  {countryOptions.map((countryOption) => (
                    <CommandItem
                      key={countryOption.code}
                      value={countryOption.label}
                      onSelect={(currentValue) => {
                        const foundCountry = countryOptions.find((item) => item.label === currentValue)
                        formik.setFieldValue("country", foundCountry?.code)
                        setOpen(false)
                      }}
                      className="cursor-pointer hover:bg-neutral-200"
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          formik.values.country === countryOption.code ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {countryOption.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </BoxFormTemplatePage>
  )
}
